import { FC, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { CountryFlag } from 'src/components/CountryFlag'
import classes from './countriesCell.module.scss'

interface CountriesCellProps {
  countries: { code?: string; name: string }[]
}

export const CountriesCell: FC<CountriesCellProps> = ({ countries }) => {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ right: number; top: number }>({
    right: 0,
    top: 0,
  })
  const btnRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return undefined
    const close = () => setOpen(false)
    const handleClickOutside = (e: MouseEvent) => {
      if (
        !btnRef.current?.contains(e.target as Node) &&
        !popoverRef.current?.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    // The popover is portaled with fixed positioning; close on page scroll so
    // it doesn't detach. Ignore scrolls inside the popover itself so the list
    // stays scrollable.
    const handleScroll = (e: Event) => {
      if (popoverRef.current?.contains(e.target as Node)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', close)
    }
  }, [open])

  const toggle = () => {
    const rect = btnRef.current?.getBoundingClientRect()
    if (rect) {
      setPos({ right: window.innerWidth - rect.right, top: rect.bottom + 6 })
    }
    setOpen((v) => !v)
  }

  if (countries.length === 0) {
    return <span className={classes.empty}>0</span>
  }

  return (
    <>
      <button
        className={classes.pill}
        onClick={toggle}
        ref={btnRef}
        type="button"
      >
        <svg
          fill="none"
          height="14"
          viewBox="0 0 24 24"
          width="14"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="12"
            cy="12"
            r="9"
            stroke="#25262D"
            strokeOpacity="0.6"
            strokeWidth="1.5"
          />
          <path
            d="M3 12h18M12 3a14 14 0 0 1 3.5 9A14 14 0 0 1 12 21a14 14 0 0 1-3.5-9A14 14 0 0 1 12 3z"
            stroke="#25262D"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeOpacity="0.6"
            strokeWidth="1.5"
          />
        </svg>
        <span className={classes.count}>{countries.length}</span>
        <span className={classes.dots}>
          <span />
          <span />
          <span />
        </span>
      </button>

      {open &&
        createPortal(
          <div
            className={classes.popover}
            ref={popoverRef}
            style={{ right: pos.right, top: pos.top }}
          >
            {countries.map((country) => (
              <div className={classes.item} key={country.name}>
                <CountryFlag
                  className={classes.flag}
                  code={country.code}
                  name={country.name}
                />
                <span className={classes.itemName}>{country.name}</span>
              </div>
            ))}
          </div>,
          document.body
        )}
    </>
  )
}
